import './TwoStageSpendAndEffectContainer.scss';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { MdZoomOutMap } from 'react-icons/md';
import { Button } from '@Components/Button';
import { ChartContainer } from '@Components/ChartContainer';
import { Placement, Size, StretchMode } from '@Components/designConstants';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Dropdown } from '@Components/Dropdown';
import { InfoTooltip } from '@Components/InfoTooltip';
import { ShadedPattern } from '@Components/FillPatterns/ShadedPattern';
import { SpendAndEffectModal } from '@Components/SpendAndEffectModal';
import { getColorMap } from '../../../utils/colors';
import { downloadPNG, exportToExcel } from '../../../utils/download-utils';
import { SpendAndEffectBarChart } from './SpendAndEffectBarChart';

// `twoStageData`/`exportFormattedData` are deeply dynamic MMM payloads; `any` is retained intentionally.
interface TwoStageContainerProps {
  colorOverrides?: Record<string, string>;
  dropdownOptions: readonly any[]; // DropdownOption
  dropdownValue: string;
  setDropdownValue: (value: string) => void;
  title: string;
  valueFormatter: (value: number) => string;
  twoStageData: any[];
  exportFormattedData?: any[];
  kpiType: string;
  groupBy: string;
  setGroupBy: (groupBy: string) => void;
  groupByOptions: { label: string; value: string }[];
  sortDropdownValue: string;
  setSortDropdownValue: (value: string) => void;
  modelDate: string;
  // Hide the Dynamics modal trigger button. Used by HROAS for Tinuiti Live.
  hideDynamicsTrigger?: boolean;
  // Per-bar row height in px. Default preserves the wrapped chart's default.
  rowHeight?: number;
  // Data for the "Dynamics" modal only, when it should differ from the collapsed bar chart's own
  // `twoStageData` (e.g. HROAS filtering Media-Driven Brand Equity out of the collapsed list while
  // still showing it in Dynamics). Defaults to `twoStageData` so existing callers are unaffected.
  modalData?: any[];
}

export const TwoStageSpendandEffectContainer = ({
  colorOverrides,
  dropdownOptions,
  dropdownValue,
  setDropdownValue,
  title,
  valueFormatter,
  twoStageData,
  exportFormattedData,
  kpiType,
  groupBy,
  setGroupBy,
  groupByOptions,
  sortDropdownValue,
  setSortDropdownValue,
  modelDate,
  hideDynamicsTrigger = false,
  rowHeight,
  modalData
}: TwoStageContainerProps): ReactElement => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const fileName = `${title.replace(/ /g, '_')}_actualized`;
  const resolvedModalData = modalData ?? twoStageData;

  const excelDownload = useCallback(() => {
    exportToExcel(exportFormattedData ?? twoStageData, fileName);
  }, [twoStageData, exportFormattedData, fileName]);

  const pngDownload = useCallback(() => {
    downloadPNG('.two-stage-spend-and-effect-container .snapshot-graph-contents', fileName);
  }, [fileName]);

  // Based on resolvedModalData (a superset when it differs from twoStageData) so every entity
  // shown in either the collapsed chart or the Dynamics modal has a defined color/pattern.
  const twoStageColorMap = useMemo(
    () => ({ ...getColorMap(resolvedModalData), ...colorOverrides }),
    [colorOverrides, resolvedModalData]
  );

  return (
    <>
      <div className='snapshot-chart-container two-stage-spend-and-effect-container'>
        <ChartContainer
          title={title}
          viewRightActions={!hideDynamicsTrigger}
          rightActions={
            hideDynamicsTrigger ? null : (
              <Button leadingElement={<MdZoomOutMap />} onClick={() => setShowModal(true)}>
                Dynamics
              </Button>
            )
          }
          subHeader={
            <>
              <div className='subheader-text'>
                <span className='label'>Actualized</span>
                <InfoTooltip>
                  The spend/effect for a given tactic after making necessary corrections for demand
                  gen {'<>'} demand capture dynamics. This is the true, causal form of each figure.
                </InfoTooltip>
              </div>
              <div className='subheader-actions'>
                {dropdownOptions && dropdownValue && setDropdownValue && (
                  <Dropdown
                    overlayPlacement={Placement.RIGHT}
                    size={Size.SM}
                    stretch={StretchMode.FIT}
                    value={dropdownValue}
                    options={dropdownOptions}
                    onChange={setDropdownValue}
                  />
                )}
                <DownloadDropdown
                  size={Size.SM}
                  onClickOptions={[excelDownload, pngDownload]}
                  menuOptions={['XLSX', 'PNG']}
                />
              </div>
            </>
          }
        >
          <div id='snapshot-graph-contents' className='snapshot-graph-contents'>
            <SpendAndEffectBarChart
              expandedModal={false}
              activeChannel='All Channels'
              barData={twoStageData}
              data={twoStageData}
              displayActualValues={false}
              valueFormatter={valueFormatter}
              colorMap={twoStageColorMap}
              barSize={20}
              groupBy={groupBy}
              rowHeight={rowHeight}
            />
            <div className='pattern-definitions'>
              <ShadedPattern color='grey' visibleTag={false} />
              <ShadedPattern color='white' backgroundColor='grey' id='Neggrey' visibleTag={false} />
              {resolvedModalData.map(item => (
                <div key={`patternDefContainer${item.name}`}>
                  <ShadedPattern color={twoStageColorMap[item.name]} visibleTag={false} />
                  <ShadedPattern
                    color='white'
                    backgroundColor={twoStageColorMap[item.name]}
                    id={`Neg${twoStageColorMap[item.name]}`}
                    visibleTag={false}
                  />
                </div>
              ))}
            </div>
          </div>
        </ChartContainer>
      </div>
      {showModal && resolvedModalData && dropdownOptions && (
        <SpendAndEffectModal
          valueFormatter={valueFormatter}
          data={[...resolvedModalData]}
          onClose={() => setShowModal(false)}
          sortOptions={[...dropdownOptions]}
          kpiType={kpiType}
          excelDownload={excelDownload}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          groupByOptions={groupByOptions}
          sortDropdownValue={sortDropdownValue}
          setSortDropdownValue={setSortDropdownValue}
          modelDate={modelDate}
        />
      )}
    </>
  );
};
